/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { IconCheckmark } from '@veneer/core';
import React, { memo, useState } from 'react';
import Tooltip from '../tooltip';
import { ModeSelectorItemProps, ModeSelectorProps } from './types';

import ListContainer from '../list-container';
import './styles.scss';
import { withAnalytics } from '@veneer/analytics';
import { generateID } from '../../base/helper';

/**
 * UI Toolkit : Mode Selector
 * @public
 */
const TModeSelector: React.FC<ModeSelectorProps> = ({
  id = generateID('clientos-ui-toolkit-mode-selector'),
  list,
  selectedValue = ''
}) => {
  const [current, setCurrent] = useState(selectedValue);

  return (
    <div
      className="clientos-ui-toolkit-mode-selector"
      id={id}
    >
      {list.map((item: ModeSelectorItemProps, index: number) => (
        <div
          key={index}
          onClick={() => setCurrent(item.value)}
          className="clientos-ui-toolkit-mode-selector__item-row"
          id={`${id}__item-${index}`}
        >
          <div
            className={`clientos-ui-toolkit-mode-selector__item ${item.value === current ? 'clientos-ui-toolkit-mode-selector__item--selected' : ''}`}
            id={`${id}__item-${index}__content`}
          >
            <div
              className="clientos-ui-toolkit-mode-selector__item--leading"
              id={`${id}__item-${index}__leading`}
            >
              {item.icon && (
                <div
                  className="clientos-ui-toolkit-mode-selector__list-icon"
                  id={`${id}__item-${index}__icon`}
                >
                  {item.icon}
                </div>
              )}
              <div
                className="clientos-ui-toolkit-mode-selector__list-label body-large"
                id={`${id}__item-${index}__label`}
              >
                {item.label}
              </div>
              {item.tooltipContent && (
                <Tooltip
                  content={item.tooltipContent}
                  id={`${id}__item-${index}__tooltip`}
                >
                  <div
                    className="clientos-ui-toolkit-mode-selector__tooltip-icon"
                    id={`${id}__item-${index}__tooltip-icon`}
                  >
                    i
                  </div>
                </Tooltip>
              )}
            </div>
            {item.value === current && (
              <div
                className="clientos-ui-toolkit-mode-selector__selected-icon"
                id={`${id}__item-${index}__selected-icon`}
              >
                <IconCheckmark size={20} />
              </div>
            )}
          </div>
          {item.content && (
            <div
              className={`clientos-ui-toolkit-mode-selector__item__content ${item.value === current && 'clientos-ui-toolkit-mode-selector__item__content--selected'}`}
              id={`${id}__item-${index}__content`}
            >
              <ListContainer items={item.content} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const ModeSelectorAnalytics = withAnalytics(TModeSelector);
export default memo(TModeSelector);
